import { QueryClient } from "@tanstack/react-query";
import {
  deleteArticleByIds,
  addArticlesBulk,
} from "@coworker/app/src/components/RoomSettings/services/articles.service";
import { mapArticleInfoToArticle } from "@coworker/app/src/components/RoomSettings/types/mappers";
import { fetchItemsInfoFromDataService } from "@coworker/app/src/components/RoomSettings/services/localCombination.service";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { fetchItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { AfterCompleteConfig } from "../components/TaskViewerSheet/AfterComplete/AfterCompleteConfig";
import { ItemNoAndType, MediaOrderedDetails } from "../types/TaskDetailTypes";
import { invalidateMediaTaskQueries } from "./queryKeyHelper";

type ManageMediaArticlesAfterCompleteConfig = Extract<
  AfterCompleteConfig,
  { id: "manage-media-articles" }
>;

// The route for a specific media/article combination becomes obsolete once
// that article is removed or replaced, so we navigate away. If the task
// spans multiple medias, prefer the one matching the page the user
// completed the task from; otherwise fall back to the first media in the
// task.
// When that media's article belongs to a group, navigate back to the
// originating group page (the same one used to reach the article) instead
// of the media's plain article-list tab.
// When the task was completed from the article search page (which isn't
// scoped to a single media), there is no sensible media tab to return to,
// so we navigate to the media maintenance home instead.
// Precondition: caller has already checked medias.length > 0.
const getRedirectMedia = (
  medias: MediaOrderedDetails[],
  currentMediaId: string | undefined
): MediaOrderedDetails =>
  medias.find((media) => media.id === currentMediaId) ?? medias[0]!;

const getRedirectUrl = (
  medias: MediaOrderedDetails[],
  currentMediaId: string | undefined,
  navigateToMediaHome: boolean
): string => {
  if (navigateToMediaHome) {
    return "/mediamaintenance";
  }
  const media = getRedirectMedia(medias, currentMediaId);
  return media.group_id
    ? `/mediamaintenance/groups/${media.group_id}?mediaId=${media.id}`
    : `/mediamaintenance/tabs/${media.id}?tab=article-list-tab`;
};

export const manageMediaArticlesAfterComplete = async (
  task: Record<string, unknown>,
  props: ManageMediaArticlesAfterCompleteConfig["props"],
  queryClient: QueryClient,
  storeId: string,
  countryId: string,
  currentMediaId: string | undefined,
  navigate: (to: string) => void,
  navigateToMediaHome = false
): Promise<void> => {
  const reasonValue = getByPath(task, props.reason) as string | null;
  const medias = getByPath(task, props.medias) as MediaOrderedDetails[] | null;
  const newItem = getByPath(task, props.newItem) as ItemNoAndType | null;

  if (
    !reasonValue ||
    !medias ||
    !Array.isArray(medias) ||
    medias.length === 0
  ) {
    return;
  }

  try {
    if (reasonValue === "remove") {
      const articleIds = medias
        .map((media) => media.article_id)
        .filter((id) => id);

      if (articleIds.length > 0) {
        await deleteArticleByIds(articleIds, queryClient);
      }
      navigate(getRedirectUrl(medias, currentMediaId, navigateToMediaHome));
      return;
    }

    if (reasonValue === "changeArticle") {
      if (!newItem || !newItem.no || !newItem.type) {
        return;
      }

      const itemInfo =
        newItem.type === "SCI"
          ? ((
              await fetchItemsInfoFromDataService(
                storeId,
                countryId,
                newItem.no
              )
            )?.[0] ?? null)
          : await fetchItemInfo(newItem.no);

      if (!itemInfo) {
        return;
      }

      for (const media of medias) {
        if (!media.article_id || !media.id || media.pieces === undefined) {
          continue;
        }

        await deleteArticleByIds([media.article_id], queryClient);

        const newArticle = {
          ...mapArticleInfoToArticle(itemInfo as Item),
          active: "ACTIVE" as const,
          mediaId: media.id,
          nbrArticles: media.pieces,
        };

        await addArticlesBulk([newArticle], media.group_id ?? "", queryClient);
      }
      navigate(getRedirectUrl(medias, currentMediaId, navigateToMediaHome));
      return;
    }
  } finally {
    await invalidateMediaTaskQueries(queryClient);
  }
};
